import React, { useState } from "react";
import "./DeliveryProgress.css";

const number = (value) => Number(value).toLocaleString("en-US", { maximumFractionDigits: 4 });
const filters = [
  ["ALL", "All Items"],
  ["DELIVERED", "Delivered"],
  ["RELEASED", "Released"],
  ["PENDING", "Not Yet Delivered"]
];

export default function DeliveryProgress({ so }) {
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const items = so?.items || [];
  const ready = items.every((item) => item.DeliveredQty != null && item.RemainingQty != null && item.ReleasedQty != null);
  const complete = (item) => Number(item.Quantity) > 0 && Number(item.RemainingQty) === 0;
  const counts = {
    ALL: items.length,
    RELEASED: items.filter((item) => Number(item.ReleasedQty) > 0).length,
    DELIVERED: items.filter(complete).length,
    PENDING: items.filter((item) => Number(item.RemainingQty) > 0).length
  };
  const query = search.trim().toLowerCase();
  const visible = items.filter((item) => {
    const matchesFilter = filter === "ALL" || (filter === "DELIVERED" ? complete(item) : filter === "RELEASED" ? Number(item.ReleasedQty) > 0 : Number(item.RemainingQty) > 0);
    const text = `${item.CatalogNo || item.CurrentCatalogNo || ""} ${item.ItemName || item.CurrentItemName || ""}`.toLowerCase();
    return matchesFilter && text.includes(query);
  });
  const cancelled = [so?.SOStatus, so?.DeliveryStatus].some((status) => ["CANCEL", "CANCELLED"].includes(String(status || "").toUpperCase()));

  return (
    <section className="so-delivery-progress" aria-label="Item delivery progress">
      <div className="sodp-heading">
        <div>
          <h3>Delivery Progress</h3>
          <p>Release and delivery progress across all delivery notes and batches.</p>
        </div>
        <span className="sodp-summary">{ready ? `${counts.DELIVERED} of ${counts.ALL} items fully delivered` : "Delivery totals unavailable"}</span>
      </div>
      {cancelled && <p className="sodp-notice">This sales order is cancelled. Remaining quantities are shown for reference.</p>}
      {!ready ? <p role="status">Update the backend and reopen this sales order to load delivery quantities.</p> : (
        <>
          <div className="sodp-controls">
            <div className="sodp-filters" role="group" aria-label="Filter delivery progress">
              {filters.map(([value, label]) => (
                <button type="button" key={value} aria-pressed={filter === value} onClick={() => setFilter(value)}>
                  {label} <span>{counts[value]}</span>
                </button>
              ))}
            </div>
            <input aria-label="Search delivery items" placeholder="Search catalog or item name..." value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>
          <p className="sodp-help">Released Qty shows items released and awaiting delivery confirmation. Not Yet Delivered includes released and partially delivered items. Remaining Qty is ordered minus delivered.</p>
          <div className="sodp-table-wrap">
            <table>
              <thead><tr><th>Catalog</th><th>Item</th><th>U/M</th><th>Ordered Qty</th><th>Released Qty</th><th>Delivered Qty</th><th>Remaining Qty</th><th>Status</th></tr></thead>
              <tbody>
                {visible.map((item) => {
                  const status = complete(item) ? "delivered" : Number(item.DeliveredQty) > 0 ? "partial" : Number(item.ReleasedQty) > 0 ? "released" : "pending";
                  return (
                    <tr key={item.SODetailID}>
                      <td>{item.CatalogNo || item.CurrentCatalogNo || "—"}</td>
                      <td>{item.ItemName || item.CurrentItemName || "—"}</td>
                      <td>{item.UnitOfMeasure || "—"}</td>
                      <td>{number(item.Quantity)}</td>
                      <td>{number(item.ReleasedQty)}</td>
                      <td>{number(item.DeliveredQty)}</td>
                      <td>{number(item.RemainingQty)}</td>
                      <td><span className={`sodp-badge sodp-${status}`}>{status === "delivered" ? "Delivered" : status === "partial" ? "Partially Delivered" : status === "released" ? "Released" : "Not Yet Delivered"}</span></td>
                    </tr>
                  );
                })}
                {visible.length === 0 && <tr><td colSpan={8} className="sodp-empty">{items.length === 0 ? "No items in this sales order." : "No items match this view."}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
